// Width scale for `ds/Modal`. Values are pixel widths handed to Mantine's Modal `size`
// prop; the wrapper caps them at 90vw for small screens. Mirrors the button-tokens.ts
// pattern — a typed scale consumed by the component so call sites pick a token, never a
// raw pixel width or a bare Mantine size keyword.
export const MODAL_SIZE = {
  sm: 400, // reserved — no current callers; confirmations use md
  md: 520, // default — most forms
  lg: 640, // larger forms, pickers
  xl: 800, // wizards, detail-heavy, uploads
  xxl: 1040, // master/detail, third-party embeds
} as const;

export type ModalSize = keyof typeof MODAL_SIZE;

// Shared open/close microanimation for every modal. `ds/Modal` applies it, and the few
// allow-listed modals that render Mantine's `Modal` directly (HIGH-RISK shells, patient flows)
// import this so the whole product animates identically. Subtle eased entrance (200ms);
// dismissal is snappier (100ms) so it gets out of the way.
export const MODAL_TRANSITION_PROPS = {
  transition: "pop",
  duration: 200,
  exitDuration: 100,
  timingFunction: "ease-out",
} as const;

// How long callers must keep a modal mounted after it closes so the exit transition can play.
// Derived from the exit transition (not a separate literal) plus a small buffer, so tuning
// `exitDuration` can't silently desync the unmount timer and clip the animation. Consumed by
// `useModalPresence`.
export const MODAL_EXIT_DURATION_MS = MODAL_TRANSITION_PROPS.exitDuration + 50;
